import React from 'react';
import { Link, useLocation } from 'react-router';
import { Button } from './ui/button';
import { ButtonTransition } from './PageTransition';
import { Menu, X, Linkedin } from 'lucide-react';
import { ClutchIcon } from './ui/ClutchIcon';
import { NavDropdown } from './NavDropdown';
import { headerNav, isGroup } from '../content/headerNav';
import { scrollToTopSmooth } from '../utils/navigationHelpers';
import { useNavigation } from '../contexts/NavigationContext';
/*
 * WebP at twice the rendered 40px height. The PNGs were 925 and 1104px wide --
 * 110 KB and 35 KB for a 40px-tall mark -- and on a slow mobile connection the
 * header logo alone was still downloading at 3.8 s, taking bandwidth from the
 * CSS and fonts the first paint waits on. The PNGs stay for the admin panel.
 */
import weAreQALogo from '@/assets/weareqa-logo.webp';
import weAreQAFooterLogo from '@/assets/weareqa-logo-footer.webp';

export function Layout({ children }: { children: React.ReactNode }) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const isEmployerHiringPage = ['/for-employers', '/hire-qa-talent'].includes(location.pathname);
  const { navigationCompleted } = useNavigation();

  // Close mobile menu on scroll and outside click (common UX patterns)
  React.useEffect(() => {
    const handleScroll = () => {
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen) {
        const target = event.target as HTMLElement;
        // Close menu if clicking outside of header area
        if (!target.closest('header')) {
          setIsMenuOpen(false);
        }
      }
    };

    if (isMenuOpen) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('click', handleClickOutside, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isMenuOpen]);

  // Favicon is managed by FaviconManager component - no need for duplicate favicon logic here

  // Scroll to top when location changes and complete navigation
  React.useEffect(() => {
    window.scrollTo(0, 0);
    // Close mobile menu on route change
    setIsMenuOpen(false);
    // Complete navigation animation
    navigationCompleted();
  }, [location.pathname, navigationCompleted]);

  const isActive = (href: string) => location.pathname === href;

  /**
   * A group highlights when any page it contains is the current route.
   */
  const isGroupActive = (hrefs: string[]) => hrefs.some((href) => location.pathname === href);

  /**
   * "Contact Us" in the footer (was "Contact Sales", which read wrong next to
   * hiring that costs the employer nothing).
   *
   * Every navigation in this component is now a `Link`, which needs no handler:
   * scroll-to-top on a route change is already done by useRouteScroll and the
   * effect above. This one survives for the case a router cannot express --
   * the link points at /contact, so on /contact itself clicking it is a no-op,
   * and a scroll to the top is the useful answer instead.
   */
  const handleContactClick = (event: React.MouseEvent) => {
    if (location.pathname !== '/contact') return;

    event.preventDefault();
    scrollToTopSmooth();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col w-full">
      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm shadow-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex shrink-0 items-center space-x-3 group bg-transparent border-0 qa-logo-animate"
            >
              <div className="flex items-center">
                <img
                  src={weAreQALogo}
                  alt="WeAreQA"
                  width={142}
                  height={40}
                  {...{ fetchpriority: 'high' }}
                  className="h-10 w-auto group-hover:scale-105 transition-transform"
                />
              </div>
            </Link>

            {/* Desktop Navigation. 1100px is where the row has room, not merely
                where it fits: 978px of content, 64px of gutters, and enough left
                over that the last item does not crowd the CTA buttons. */}
            <nav className="hidden shrink-0 min-[1100px]:flex items-center gap-3">
              {headerNav.map((entry) =>
                isGroup(entry) ? (
                  <NavDropdown
                    key={entry.label}
                    group={entry}
                    isActive={isGroupActive(entry.items.map((item) => item.href))}
                  />
                ) : (
                  <Link
                    key={entry.href}
                    to={entry.href}
                    aria-current={isActive(entry.href) ? 'page' : undefined}
                    className={`whitespace-nowrap px-3 py-2 rounded-md text-[15px] font-medium bg-transparent border-0 qa-nav-item ${
                      isActive(entry.href)
                        ? 'text-qa-blue-600 bg-qa-blue-50'
                        : 'text-muted-foreground hover:bg-qa-blue-50'
                    }`}
                  >
                    {entry.label}
                  </Link>
                )
              )}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden shrink-0 min-[1100px]:flex items-center space-x-3">
              {!isEmployerHiringPage && (
                <ButtonTransition
                  to="/apply-qa-engineer"
                  className="bg-qa-yellow-400 text-qa-blue-900 hover:bg-qa-yellow-500 border-0 whitespace-nowrap px-4 py-2 rounded-md text-[15px] font-medium qa-nav-button"
                >
                  Apply as QA
                </ButtonTransition>
              )}
              <ButtonTransition
                to="/for-employers"
                className="bg-qa-blue-600 hover:bg-qa-blue-700 text-white whitespace-nowrap px-4 py-2 rounded-md text-[15px] font-medium qa-nav-button"
              >
                Hire QA Talent
              </ButtonTransition>
            </div>

            {/* Mobile menu button */}
            <div className="min-[1100px]:hidden">
              <Button variant="ghost" size="icon" aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={isMenuOpen} onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/*
          Mobile Navigation.

          Rendered whether or not it is open, and hidden with the `hidden`
          attribute instead. It used to be `{isMenuOpen && ...}`, which meant the
          dropdown items existed in the document only while a panel was open --
          and the desktop panels are mounted on open too. So no crawler ever saw
          a link to /about, /blog, /for-employers or any industry page: it had to
          click to find them, which it cannot do.

          `hidden` rather than a class, because it also takes the links out of
          the tab order and the accessibility tree while the menu is shut, which
          is what the conditional render was really buying.
        */}
        <div hidden={!isMenuOpen}>
          <div className="min-[1100px]:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t bg-background/95 backdrop-blur-sm">
            <div className="px-2 pt-2 pb-3">
              {/* A hover dropdown has no touch equivalent, so each group renders
                  as a labelled block of links rather than a collapsed panel.
                  py-2.5 rather than py-2: it puts the tap target at ~41px, near
                  the 44px these want to be. */}
              {headerNav.map((entry, index) => (
                <React.Fragment key={isGroup(entry) ? entry.label : entry.href}>
                  {index > 0 && <div className="my-2 h-px bg-border" aria-hidden="true" />}

                  {isGroup(entry) ? (
                    <>
                      <p className="px-3 pb-1 text-micro font-extrabold uppercase text-muted-foreground">
                        {entry.label}
                      </p>
                      {entry.items.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          onClick={() => setIsMenuOpen(false)}
                          aria-current={isActive(item.href) ? 'page' : undefined}
                          className={`block w-full text-left pl-6 pr-3 py-2.5 rounded-md text-base font-medium transition-colors bg-transparent border-0 cursor-pointer ${
                            isActive(item.href)
                              ? 'text-qa-blue-600 bg-qa-blue-50'
                              : 'text-muted-foreground hover:text-qa-blue-600 hover:bg-qa-blue-50'
                          }`}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </>
                  ) : (
                    <Link
                      to={entry.href}
                      onClick={() => setIsMenuOpen(false)}
                      aria-current={isActive(entry.href) ? 'page' : undefined}
                      className={`block w-full text-left px-3 py-2.5 rounded-md text-base font-medium transition-colors bg-transparent border-0 cursor-pointer ${
                        isActive(entry.href)
                          ? 'text-qa-blue-600 bg-qa-blue-50'
                          : 'text-muted-foreground hover:text-qa-blue-600 hover:bg-qa-blue-50'
                      }`}
                    >
                      {entry.label}
                    </Link>
                  )}
                </React.Fragment>
              ))}
              <div className="pt-4 pb-2 space-y-2">
                {!isEmployerHiringPage && (
                  <ButtonTransition
                    to="/for-qa-engineers"
                    onClick={() => setIsMenuOpen(false)}
                    className="w-full bg-qa-yellow-400 text-qa-blue-900 hover:bg-qa-yellow-500 border-0 px-4 py-2 rounded-md font-medium"
                  >
                    Apply as QA
                  </ButtonTransition>
                )}
                <ButtonTransition
                  to="/for-employers"
                  onClick={() => setIsMenuOpen(false)}
                  className="w-full bg-qa-blue-600 hover:bg-qa-blue-700 text-white px-4 py-2 rounded-md font-medium"
                >
                  Hire QA Talent
                </ButtonTransition>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link
                to="/"
                className="flex items-center mb-4 bg-transparent border-0 cursor-pointer"
              >
                <img
                  src={weAreQAFooterLogo}
                  alt="WeAreQA"
                  width={173}
                  height={40}
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-auto hover:scale-105 transition-transform"
                />
              </Link>
              <p className="mb-4 leading-relaxed text-[rgba(194,205,221,1)]">
                Quality engineering for teams shipping regulated software, and QA hiring with no
                recruitment fee.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">For Professionals</h3>
              <ul className="space-y-2 text-[rgba(194,205,221,1)]">
                <li>
                  <Link
                    to="/for-qa-engineers"
                    className="hover:text-qa-yellow-400 transition-colors bg-transparent border-0 cursor-pointer"
                  >
                    Find Jobs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="hover:text-qa-yellow-400 transition-colors bg-transparent border-0 cursor-pointer"
                  >
                    Career Resources
                  </Link>
                </li>
                <li>
                  {/* A real destination, not "#". It used to be an in-page
                      anchor handled entirely in JS; see the note on
                      /for-qa-engineers below. */}
                  <Link
                    to="/for-qa-engineers"
                    className="hover:text-qa-yellow-400 transition-colors cursor-pointer"
                  >
                    Success Stories
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">For Employers</h3>
              <ul className="space-y-2 text-[rgba(194,205,221,1)]">
                <li>
                  <Link
                    to="/for-employers"
                    className="hover:text-qa-yellow-400 transition-colors bg-transparent border-0 cursor-pointer"
                  >
                    Hire QA Talent
                  </Link>
                </li>
                <li>
                  {/* href="#" told crawlers nothing and left /contact
                      reachable only from the header dropdown. The handler now
                      only intervenes when we are already on /contact, where a
                      navigation would be a no-op and a smooth scroll is the
                      useful response. */}
                  <Link
                    to="/contact"
                    onClick={handleContactClick}
                    className="hover:text-qa-yellow-400 transition-colors cursor-pointer"
                  >
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="hover:text-qa-yellow-400 transition-colors bg-transparent border-0 cursor-pointer"
                  >
                    Hiring Guide
                  </Link>
                </li>
                <li>
                  {/* The page had no inbound link from anywhere on the site.
                      Its intended entry point is the conference popup, whose
                      destination comes from the banners table -- so with no
                      banner active nothing reached it, and a popup rendered
                      after an API call is not a path a crawler can follow
                      either. Here it is buyer-facing and on every page. */}
                  <Link
                    to="/ai-testing-use-cases"
                    className="hover:text-qa-yellow-400 transition-colors bg-transparent border-0 cursor-pointer"
                  >
                    AI Testing Use Cases
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 bg-[rgba(194,205,221,0)]">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6">
              <p className="text-[rgba(194,205,221,1)]">
                &copy; {new Date().getFullYear()} WeAreQA. All rights reserved.
              </p>
              <div className="flex items-center space-x-6 mt-4 md:mt-0">
                <div className="flex items-center space-x-4">
                  <span className="text-[rgba(194,205,221,1)] text-sm text-[14px]">Follow us:</span>

                  <a
                    href="https://www.linkedin.com/company/weareqa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[rgba(194,205,221,1)] hover:text-qa-yellow-400 transition-colors"
                    aria-label="Follow us on LinkedIn"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>

                  <a
                    href="https://clutch.co/profile/weareqa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[rgba(194,205,221,1)] hover:text-qa-yellow-400 transition-colors"
                    aria-label="Read our reviews on Clutch"
                  >
                    <ClutchIcon className="h-5 w-5" />
                  </a>
                </div>
                <div className="flex space-x-6">
                  <Link
                    to="/privacy-policy"
                    className="text-[rgba(194,205,221,1)] hover:text-qa-yellow-400 transition-colors"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/terms-of-service"
                    className="text-[rgba(194,205,221,1)] hover:text-qa-yellow-400 transition-colors"
                  >
                    Terms of Service
                  </Link>
                  <button
                    type="button"
                    className="text-[rgba(194,205,221,1)] hover:text-qa-yellow-400 transition-colors"
                    onClick={() => window.dispatchEvent(new Event('waq:cookie-preferences'))}
                  >
                    Cookie preferences
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
